import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, HandCoins, Trash2, History, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { EmptyState, StatusBadge, Section, StatCard } from "@/components/Primitives";
import { StaggerRoot, StaggerItem } from "@/components/motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function Debtors() {
  const { t, money } = useI18n();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", amount: "", notes: "" });
  const [payOpen, setPayOpen] = useState(false);
  const [paying, setPaying] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [history, setHistory] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/debtors");
      setList(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = list
    .filter((d) => filterStatus === "all" ? true : d.status === filterStatus)
    .filter((d) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return d.name.toLowerCase().includes(q) || (d.phone || "").includes(q);
    });

  const totalDebt = list.reduce((s, d) => s + d.remaining_debt, 0);

  const save = async () => {
    if (!form.name) { toast.error(t("error")); return; }
    try {
      await api.post("/debtors", { ...form, amount: Number(form.amount || 0) });
      toast.success(t("success"));
      setOpen(false);
      setForm({ name: "", phone: "", address: "", amount: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pay = async () => {
    if (!Number(payAmt)) return;
    try {
      await api.post(`/debtors/${paying.id}/pay`, { amount: Number(payAmt), notes: "" });
      toast.success(t("success"));
      setPayOpen(false); setPaying(null); setPayAmt("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/debtors/${id}`);
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const showHistory = async (d) => {
    try {
      const r = await api.get(`/debtors/${d.id}`);
      setHistory(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("debtors")}</h1>
          <p className="text-sm text-gray-500 mt-1">{list.length} {t("user")}</p>
        </div>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]">
          <Plus className="w-4 h-4" /> {t("addUser")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={HandCoins} label={t("totalDebt")} value={money(totalDebt)} accent="#DC2626" />
        <StatCard icon={HandCoins} label={t("debtors")} value={list.length} accent="#F59E0B" />
        <StatCard icon={HandCoins} label={t("paid")} value={list.filter((d) => d.status === "paid").length} accent="#16A34A" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="w-full h-11 pr-10 pl-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#071A33]" />
        </div>
        {["all", "active", "partial", "paid"].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1.5 rounded-full text-sm border ${filterStatus === s ? "bg-[#071A33] text-white border-[#071A33]" : "bg-white text-gray-700 border-gray-200"}`}>
            {s === "all" ? t("allCategories") : t(s)}
          </button>
        ))}
      </div>

      <Section title={t("debtors")}>
        {filtered.length === 0 ? <EmptyState icon={HandCoins} title={t("empty")} /> : (
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-right text-sm">
              <thead className="text-xs text-gray-500 bg-gray-50">
                <tr>
                  <th className="p-3">{t("name")}</th>
                  <th className="p-3">{t("phone")}</th>
                  <th className="p-3">{t("totalDebt")}</th>
                  <th className="p-3">{t("totalPaid")}</th>
                  <th className="p-3">{t("remainingDebt")}</th>
                  <th className="p-3">{t("status")}</th>
                  <th className="p-3">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} className="border-t border-gray-100">
                    <td className="p-3 text-gray-900 font-medium">{d.name}</td>
                    <td className="p-3 text-gray-500 tabular">{d.phone || "-"}</td>
                    <td className="p-3 tabular">{money(d.total_debt)}</td>
                    <td className="p-3 tabular text-emerald-600">{money(d.total_paid)}</td>
                    <td className="p-3 tabular font-bold text-rose-600">{money(d.remaining_debt)}</td>
                    <td className="p-3"><StatusBadge status={d.status} label={t(d.status)} /></td>
                    <td className="p-3 flex gap-2">
                      {d.remaining_debt > 0 && (
                        <button onClick={() => { setPaying(d); setPayAmt(String(d.remaining_debt)); setPayOpen(true); }} className="px-3 py-1.5 rounded-lg bg-[#071A33] text-white text-xs">{t("addPayment")}</button>
                      )}
                      <button onClick={() => showHistory(d)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><History className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(d.id)} className="p-2 rounded-lg border border-rose-100 text-rose-600 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t("addUser")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("name")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("phone")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t("address")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder={t("amount")} className="w-full h-11 px-3 rounded-xl border border-gray-200" />
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t("notes")} className="w-full p-3 rounded-xl border border-gray-200" />
          </div>
          <DialogFooter className="flex-row-reverse">
            <button onClick={save} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("save")}</button>
            <button onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent dir="rtl" className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{t("addPayment")}: {paying?.name}</DialogTitle></DialogHeader>
          <input data-testid="debtor-pay-amount" type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-gray-200 text-lg tabular font-bold" />
          <DialogFooter className="flex-row-reverse">
            <button data-testid="confirm-debtor-pay" onClick={pay} className="px-5 py-2.5 rounded-xl bg-[#071A33] text-white">{t("confirm")}</button>
            <button onClick={() => setPayOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200">{t("cancel")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent dir="rtl" className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{t("debtHistory")}: {history?.debtor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {history?.transactions?.length === 0 && <EmptyState title={t("empty")} />}
            {history?.transactions?.map((tx) => (
              <div key={tx.id} className="flex justify-between items-center border-b border-gray-100 py-2">
                <div>
                  <div className="text-sm font-medium">{tx.type === "debt" ? t("debt") : t("paid")}</div>
                  <div className="text-xs text-gray-500">{(tx.date || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className={`font-bold tabular ${tx.type === "debt" ? "text-rose-600" : "text-emerald-600"}`}>
                  {tx.type === "debt" ? "+" : "-"} {money(tx.amount)}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </StaggerRoot>
  );
}
